/**
 * NgwEditorQuotedButton — `IQuotedButtonControl` 의 NgwEditor 구현 (PR-N4).
 *
 * D9 결정 (03 §2.4): `install` / `isInstalled` 명명. TinyMCE 의 `setShowQuotedButton(state)` 의
 * state 인자가 미사용이라는 발견에서 명칭 정정.
 *
 * ### NgwEditor 의 구현 시맨틱
 *
 * - **install**: 인용 영역 (quoted_header + quoted_body) 을 doc 에 lazy 삽입하는 콜백을 어댑터
 *   인스턴스에 등록한다. EmailForm 의 답장 흐름이 메시지 이력 로드 완료 시 본 콜백을 트리거
 *   (`appendMsgHistory` — PR-N6 의 event wiring 에서 결합) 하면 콜백이 `insertMailLayout`
 *   명령으로 quoted_header / quoted_body 노드를 doc 에 append.
 * - **skip(true)**: install 등록 후에도 트리거 시 동작하지 않음 (멱등 보장). 사용자가 인용
 *   영역을 명시적으로 거부했거나 답장 본문이 아닌 새 메일 작성 모드일 때 사용.
 * - **isInstalled**: install 호출 후 remove 미호출 상태인지.
 * - **setText**: NodeView 토글 버튼의 라벨 텍스트. 본 PR-N4 단계에서는 인스턴스 멤버에만 보존
 *   (toolbar / NodeView 와의 wiring 은 PR-N6 의 event handler 단계).
 * - **remove**: 콜백 등록 해제 + 텍스트/skip 플래그 모두 초기화.
 *
 * 본 클래스의 `install` 콜백은 별도 트리거가 없어도 안전 — 콜백만 보유. PR-N6 에서
 * `IEditorEventHandler.messageHistoryAppended` 의 NgwEditor wiring 이 호출자.
 *
 * PoC 의 `quotedBodyNodeView` 의 collapsed attr 토글은 별개의 시맨틱축 (이미 doc 에 있는
 * quoted_body 의 보기/숨기기) — 본 클래스와 직교한다.
 */

import type { IQuotedButtonControl } from '../IQuotedButtonControl'
import type { NgwEditorAdapter } from './NgwEditorAdapter'

/** quotedButton.install 시 보존되는 lazy 삽입 콜백 형태. */
export interface QuotedInstallPayload {
  quotedHeader?: string
  quotedBody?: string
}

export class NgwEditorQuotedButton implements IQuotedButtonControl {
  private readonly adapter: NgwEditorAdapter

  private installed = false
  private skipFlag = false
  /** 버튼 라벨 텍스트 — PR-N6 의 toolbar / NodeView wiring 에서 소비. */
  private text = ''

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  install(): void {
    this.installed = true
  }

  isInstalled(): boolean {
    return this.installed
  }

  remove(): void {
    this.installed = false
    this.skipFlag = false
    this.text = ''
  }

  setText(text: string): void {
    this.text = text
  }

  /** PR-N6 의 toolbar / NodeView wiring 에서 사용. @internal */
  getText(): string {
    return this.text
  }

  skip(use: boolean): void {
    this.skipFlag = use
  }

  /** skip 플래그 현재 값. @internal — PR-N6 wiring 시점 노출용. */
  isSkipped(): boolean {
    return this.skipFlag
  }

  /**
   * install 된 콜백 트리거 — PR-N6 에서 messageHistoryAppended event 가 호출.
   * `skip(true)` 상태면 멱등하게 NoOp.
   *
   * @internal — 외부 IQuotedButtonControl 표면에 포함되지 않음. 어댑터 내부 wiring 전용.
   */
  triggerInsert(payload: QuotedInstallPayload): boolean {
    if (this.installed === false) return false
    if (this.skipFlag === true) return false

    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return false

    return handle.executeCommand('insertMailLayout', {
      quotedHeader: payload.quotedHeader,
      quotedBody: payload.quotedBody,
    })
  }
}
